// @ts-check
// REGION store — Zustand (Overmind retirement, Phase C2 part 2). The CLIENT-side,
// per-visitor region/geo state that the former Overmind store held as
// `countries`, `shippingCountry` and the client half of `newRegion` — plus the
// `served` shippability DERIVATION. This is the RISKIEST slice of the retirement
// (it drives the country picker, the region/currency the checkout prices against,
// and the can't-ship buy-block), so every field/action below reproduces the
// former Overmind operator semantics EXACTLY (grep overmind/operators.js +
// overmind/actions.js for each).
//
// WHY ZUSTAND (not a server-props provider) for these:
//   - `countries` is fetched POST-MOUNT client-side (/api/getCountries — it is
//     NOT in the SSR region price bundle; only shippingInfo/newRegion are), so it
//     is empty in the SSR HTML today and must stay empty (no new SSR fetch).
//   - `shippingCountry` is per-user (cookie/geo overlay) and deliberately absent
//     from the SSR HTML (cache-safety #188), overlaid only AFTER mount.
//   - Both are read by the FOOTER country picker, which renders in the server
//     <SiteChrome> shell OUTSIDE the page-content provider tree — so a React
//     context provider (like NamedPrices/RegionGeo) could not reach it. A GLOBAL
//     Zustand store reaches both the chrome picker and the in-page consumers.
//
// SSR-SAFETY — every field starts at its EMPTY default (null / []). Nothing here
// is read during render on the server (the store is a client module singleton;
// mutating it per-request on the server would leak across concurrent requests —
// the classic Zustand-SSR hazard). The region-DETERMINISTIC values the SSR HTML
// needs (shippingInfo, the SSR newRegion) are threaded as SERVER PROPS via
// components/RegionGeo instead; this store only ever OVERLAYS the per-visitor
// values POST-MOUNT (getCountries + overlayShippingCountry + initClientRegion run
// from client effects), so the SSR render and the first client render both see
// the empty default → no React #418/#423 and no per-user cache leak.
//
// `newRegion` — THE TWO FACES. `newRegion` is the COUNTRY code the page prices
// against (US/DE/AU/…), NOT the region slug. Its SSR value (region-deterministic,
// = regionToFetchCountry(urlRegion) on a commerce page, null on a content page) is
// a SERVER PROP (components/RegionGeo). This store holds only the CLIENT-resolved /
// client-MUTATED value: seeded post-mount from that server prop (commerce) or the
// URL/sessionStorage (content), updated on the in-place payment-page region change
// (selectedCountryChanged) and by getCountries' geo-detected fallback. The
// useRegionCountryCode() hook composes `store.newRegion ?? serverProp` so the SSR
// shell uses the server prop and the client prefers the resolved store value.
import { create } from 'zustand'
import { getCookie } from 'cookies-next'
import {
  stripRegionFromPath,
  isRegion,
  regionToFetchCountry,
  countryToRegion,
  COUNTRY_COOKIE,
} from '../utils/regions'

/**
 * The exact `state.served` derivation (overmind/state.js): can we SHIP to the
 * visitor's chosen shipping country? Defaults to TRUE when there is no chosen
 * country or the country/map is not yet resolved (never wrongly block a served
 * visitor), and returns false ONLY for a country whose backend row carries
 * `served === false`.
 * @param {string|null|undefined} shippingCountry
 * @param {any[]} countries
 * @returns {boolean}
 */
export function deriveServed(shippingCountry, countries) {
  const cc = shippingCountry
  if (!cc) return true
  const row = (countries || []).find((c) => c && c.code === String(cc).toUpperCase())
  return row && row.served === false ? false : true
}

export const useRegionStore = create((set, get) => ({
  // ---- country option list (post-mount /api/getCountries; SSR-empty) ----
  /** @type {any[]} the picker option list ({ code, name, store, served, states,... }) */
  countries: [],
  /** true while the /api/getCountries fetch is in flight (mirrors state.countriesLoading) */
  countriesLoading: false,

  // ---- the visitor's precise shipping country (cookie/geo overlay; SSR-null) ----
  /** @type {string|null} the picker's displayed country code (post-mount only) */
  shippingCountry: null,

  // ---- the CLIENT-resolved pricing country (see header). SSR-null; the SSR value
  //      comes from the RegionGeo server prop. Seeded/updated post-mount. ----
  /** @type {string|null} */
  newRegion: null,

  // ---- the CLIENT-refined region SHIPPING INFO (Overmind retirement, Phase D2).
  //      The region-DETERMINISTIC shippingInfo the SSR HTML needs comes from the
  //      RegionGeo server prop; this field holds ONLY the POST-MOUNT refinement
  //      (the former Overmind state.shippingInfo write): the sub-region overlay
  //      (refineForShippingCountry — FR/IT on /eu) and the in-place payment-page
  //      region change (useRegionChange). SSR-empty ({}) so useRegionShippingInfo
  //      returns the server prop on SSR + first render (hydration-safe), and the
  //      refined value only once this fills post-mount. ----
  /** @type {any} */
  shippingInfo: {},

  // ---- the in-place region-change loading flag (Overmind retirement, Phase D2).
  //      Mirrors the former GLOBAL Overmind state.isLoading that the
  //      selectedCountryChanged pipe raised to disable the state/province picker
  //      during the region refetch. Read by StatesAutocomplete. SSR-false. ----
  isRegionChanging: false,

  /** true once overlayShippingCountry has run (post-mount) */
  _hasOverlaid: false,

  /** @type {Promise<void>|null} the in-flight getCountries fetch (see below) */
  _countriesPromise: null,

  /**
   * Load the country option list. Mirrors operators.getCountries EXACTLY: only
   * fetches when the list is empty AND not already loading (idempotent — safe to
   * call from BOTH app/providers.jsx's global bootstrap AND RegionPageClient's
   * per-page effect). On success it fills `countries` and, when the client
   * pricing country is still unresolved, seeds it from the geo-detected code (the
   * former `if (state.newRegion == null) state.newRegion = data.detectedCountryCode`
   * + sessionStorage write). Fail-open to [] on error.
   *
   * AWAITABLE (the /world picker fix): a second caller that arrives while the
   * first fetch is in flight now gets that SAME promise back instead of returning
   * immediately, so `await getCountries()` always resolves with the list actually
   * loaded. overlayShippingCountry depends on this: it must resolve a country's
   * BACKEND store, and the legacy sync fallback can never return 'world' (it maps
   * everything outside US/CA/AU/NZ to 'eu'), so overlaying before the list landed
   * blanked the picker for every /world country. Idempotence is unchanged — the
   * empty-list + in-flight guards still prevent a second fetch.
   */
  getCountries: async () => {
    if (get().countries.length > 0) {
      return
    }
    if (get()._countriesPromise) {
      return get()._countriesPromise
    }
    const promise = (async () => {
      set({ countriesLoading: true })
      try {
        const res = await fetch('/api/getCountries')
        if (!res.ok) {
          throw new Error(`getCountries ${res.status}`)
        }
        const data = await res.json()
        set({ countries: Array.isArray(data.countries) ? data.countries : [] })
        if (get().newRegion == null && data.detectedCountryCode) {
          set({ newRegion: data.detectedCountryCode })
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('newRegion', data.detectedCountryCode)
          }
        }
      } catch (e) {
        set({ countries: [] })
      } finally {
        set({ countriesLoading: false, _countriesPromise: null })
      }
    })()
    set({ _countriesPromise: promise })
    return promise
  },

  /**
   * Seed the client pricing country POST-MOUNT. Mirrors operators.getRegionFromStorage
   * (RENDER-SAFE only there; here it runs in a client effect): prefer the SSR
   * server-prop value (region-deterministic, = the country the page priced
   * against, so a hydrated commerce page keeps its exact region), else derive from
   * a region-prefixed URL, else sessionStorage; a bare content page is left null
   * for getCountries' geo-detected fallback. No-op once resolved.
   * @param {string|null} serverNewRegion  the RegionGeo server prop (fetchCountry|null)
   */
  initClientRegion: (serverNewRegion) => {
    if (get().newRegion) {
      return
    }
    if (serverNewRegion) {
      set({ newRegion: serverNewRegion })
      return
    }
    if (typeof window !== 'undefined') {
      const { region } = stripRegionFromPath(window.location.pathname)
      if (isRegion(region)) {
        set({ newRegion: regionToFetchCountry(region) })
        return
      }
      const stored = window.sessionStorage.getItem('newRegion')
      if (stored) {
        set({ newRegion: stored })
      }
    }
  },

  /**
   * POST-MOUNT resolution of the picker's DISPLAYED country. Mirrors
   * operators.overlayShippingCountryFromCookie EXACTLY: an explicit COUNTRY_COOKIE
   * pick wins; otherwise the geo-IP country (from /api/getRegion) is shown as the
   * default (NEVER persisted — the cookie is the user's explicit choice). Either
   * value is displayed ONLY when it belongs to the page's region store, else the
   * picker stays a placeholder (null) so currency + picker agree. Cache-safe: runs
   * only post-mount, writes no cookie. Idempotent (guarded by _hasOverlaid).
   *
   * The country LIST is awaited first (getCountries is idempotent + awaitable):
   * the region check below is only correct against the BACKEND `store` field, and
   * with an empty list every lookup fell through to the legacy sync
   * countryToRegion — which returns us|eu|au and NEVER 'world'. That blanked the
   * picker for all 216 /world countries (and, with shippingCountry left null,
   * silently disabled the sub-region ships-from refinement and the served=false
   * can't-ship buy-block). A list fetch that fails still leaves [] and falls back
   * to the legacy split exactly as before.
   */
  overlayShippingCountry: async () => {
    if (typeof window === 'undefined' || get()._hasOverlaid) {
      return
    }
    set({ _hasOverlaid: true })
    await get().getCountries()
    const { region: urlRegion } = stripRegionFromPath(window.location.pathname)
    const pageRegion = isRegion(urlRegion) ? urlRegion : null
    // The store a country belongs to (backend-authoritative: prefer the `store`
    // on the country's row; fall back to the legacy sync countryToRegion).
    const storeOf = (cc) => {
      const code = String(cc || '').toUpperCase()
      const row = (get().countries || []).find((c) => c && c.code === code)
      return row && isRegion(row.store) ? String(row.store).toLowerCase() : countryToRegion(code)
    }
    const displayableFor = (cc) =>
      !pageRegion || storeOf(cc) === pageRegion ? String(cc).toUpperCase() : null

    const cookieCountry = getCookie(COUNTRY_COOKIE)
    if (cookieCountry) {
      set({ shippingCountry: displayableFor(cookieCountry) })
      return
    }
    // No explicit pick: show the geo-IP country as the default (display only).
    try {
      const res = await fetch('/api/getRegion')
      if (!res.ok) {
        return
      }
      const data = await res.json()
      const geoCountry = data && data.country
      if (geoCountry) {
        set({ shippingCountry: displayableFor(String(geoCountry).toUpperCase()) })
      }
    } catch (e) {
      /* geo lookup is best-effort; leave the placeholder */
    }
  },

  /**
   * Optimistic, immediate picker update on an explicit user pick. Mirrors
   * actions.setShippingCountry: upper-cased, or null to clear.
   * @param {string|null} country
   */
  setShippingCountry: (country) =>
    set({ shippingCountry: country ? String(country).toUpperCase() : null }),

  /**
   * Set the client pricing country (the in-place payment-page region change).
   * Mirrors operators.setRegionInStorage: update newRegion + persist to
   * sessionStorage. Called from the Overmind selectedCountryChanged pipe (via
   * operators.syncRegionStoreOnCountryChange) so the region-store consumers track
   * the change the Overmind offers refetch prices against.
   * @param {string|null} newRegion
   */
  setNewRegion: (newRegion) => {
    set({ newRegion })
    if (typeof window !== 'undefined' && newRegion) {
      window.sessionStorage.setItem('newRegion', newRegion)
    }
  },

  /**
   * Set the POST-MOUNT refined region shipping info (the sub-region overlay + the
   * in-place region change). Mirrors the former Overmind `state.shippingInfo = ...`
   * writes (operators.getShippingInfo / refetchShopItemsForShippingCountry).
   * @param {any} shippingInfo
   */
  setShippingInfo: (shippingInfo) => set({ shippingInfo: shippingInfo || {} }),

  /**
   * Toggle the in-place region-change loading flag (the picker disable during the
   * region refetch). Mirrors the former selectedCountryChanged `state.isLoading`.
   * @param {boolean} changing
   */
  setRegionChanging: (changing) => set({ isRegionChanging: !!changing }),
}))

// ---- selectors ----
/** @param {{ countries: any[] }} s */
export const selectCountries = (s) => s.countries
/** @param {{ shippingCountry: string|null }} s */
export const selectShippingCountry = (s) => s.shippingCountry
/** @param {{ countriesLoading: boolean }} s */
export const selectCountriesLoading = (s) => s.countriesLoading
/** The post-mount refined region shipping info. @param {{ shippingInfo: any }} s */
export const selectShippingInfo = (s) => s.shippingInfo
/** The in-place region-change loading flag. @param {{ isRegionChanging: boolean }} s */
export const selectRegionChanging = (s) => s.isRegionChanging
/** The `served` shippability flag (derived). @param {{ shippingCountry: string|null, countries: any[] }} s */
export const selectServed = (s) => deriveServed(s.shippingCountry, s.countries)

// ---- convenience hooks ----
/**
 * The `served` shippability flag as a hook (the former `state.served` read).
 * Recomputes from shippingCountry + countries; both are SSR-empty so this is
 * `true` on the server and the first client render (no #418/#423), refined
 * post-mount once the overlay + country list resolve.
 * @returns {boolean}
 */
export const useServed = () => useRegionStore(selectServed)
